// @ts-check
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  signInWithPopup,
  unlink,
  GoogleAuthProvider,
} from "firebase/auth"
import { auth } from "../config/firebase.config"
import { api } from "./base/api.service"
import { API_PERFIL } from "../consts/apiRoutes.consts"
import { Role } from "../consts/roles.consts"
import { SERVER_ERROR_CODES, SERVER_ERRORS } from "../consts/httpStatus.consts"
const { BACKEND_UNAVAILABLE, SERVER_UNAVAILABLE } = SERVER_ERROR_CODES
/**
 * Clase para manejar errores personalizados en la autenticación y parecerse a firebaseAuth
 */
class CustomError extends Error {
  constructor(message, code) {
    super(message)
    this.code = code
  }
}

/**
 *
 * @param {string} email
 * @param {string} password
 * @param {boolean} rememberme
 * @returns
 */
export function loginWithEmail(email, password, rememberme = false) {
  const persistence =
    rememberme ? browserLocalPersistence : browserSessionPersistence
  return setPersistence(auth, persistence).then(() => {
    return signInWithEmailAndPassword(auth, email, password).then((res) => {
      const { uid } = res.user
      return { uid }
    })
  })
}

export async function logout() {
  auth.signOut()
}

/**
 * Processes the firebase user and fetches additional profile data
 * @param {import('firebase/auth').User | null} user 
 * @param {function(import('./auth.service.d.ts').UserData | null): void} callback 
 */
export async function processUserSession(user, callback) {
  /** @type {import('./auth.service.d.ts').UserData | null} */
  let data = null
  if (user) {
    try {
      let {
        id,
        name,
        lastName,
        email,
        nameTag,
        hasTimeRegister,
        teamMemberID,
        userRole,
        projectRole,
        hasCompletedBasicTutorial,
        hasCompletedTaskTutorial,
        teamAreaId,
        active,
        leaderProjects,
        workAnniversary,
      } = await getUserData()
      if ((!id && !name && !userRole) || !active) {
        await logout()
        throw new Error("User not found")
      }
      if (projectRole === "admin" && !hasTimeRegister) {
        projectRole = "adminWithoutReport"
      }
      data = {
        id,
        uid: user.uid,
        nombre: `${name} ${lastName}`,
        email,
        nameTag,
        teamMemberID,
        rol: userRole,
        projectRole,
        hasCompletedBasicTutorial,
        hasCompletedTaskTutorial,
        teamAreaId,
        leaderProjects,
        workAnniversary: workAnniversary?.split("T")[0],
        isAdminWithoutReport: () => projectRole === Role.ADMIN_WITHOUT_REPORT,
        isAdmin: () => {
          return (
            projectRole === Role.ADMIN ||
            projectRole === Role.ADMIN_WITHOUT_REPORT
          )
        },
        isTeamMember: () => {
          return (
            projectRole !== Role.ADMIN &&
            projectRole !== Role.ADMIN_WITHOUT_REPORT
          )
        },
        isTeamLeader: () => {
          return projectRole === Role.TEAM_LEADER
        },
      }
    } catch (error) {
      console.error("Error getting user data", error)
    }
  }
  callback(data)
}

/**
 *
 * @param {function(import('./auth.service.d.ts').UserData | null): void} callback
 * @returns {import('firebase/auth').Unsubscribe}
 */
export function onAuthStateChanged(callback) {
  return auth.onIdTokenChanged(async (user) => {
    await processUserSession(user, callback)
  })
}

export function getToken() {
  return auth.currentUser?.getIdToken()
}

async function getUserData() {
  try {
    const response = await api.get(API_PERFIL)
    if (!response.ok) {
      throw new CustomError(
        SERVER_ERRORS[SERVER_UNAVAILABLE],
        SERVER_UNAVAILABLE
      )
    }
    const data = await response.json()
    return data.result || {}
  } catch (error) {
    if (error instanceof CustomError) {
      throw error
    }
    throw new CustomError(
      SERVER_ERRORS[BACKEND_UNAVAILABLE],
      BACKEND_UNAVAILABLE
    )
  }
}

export const validateActiveUser = async () => {
  const { active } = await getUserData()
  if (!active) {
    await logout()
    throw new Error("User not active")
  }
}

/**
 *
 * @param {string} email
 * @returns
 */
export function recoverPassword(email) {
  return sendPasswordResetEmail(auth, email)
}

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider()
  const result = await signInWithPopup(auth, provider)
  const { uid } = result.user
  return { uid }
}

export async function unlinkGoogleAccount() {
  if (auth.currentUser) {
    await unlink(auth.currentUser, "google.com")
  } else {
    throw new Error("No user is currently signed in.")
  }
}
